/**
 * Attachments and comments data: state.ext.attachments and state.ext.comments from the store
 * (GET /api/state, then SSE 'ext'), and the calls that change them. Uploads stream the raw file
 * with progress (XMLHttpRequest). Demo mode (?mock=1) keeps everything in the browser.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ATTACHMENTS_EXT,
  ATTACHMENT_MAX_BYTES,
  COMMENTS_EXT,
  CSRF_HEADER,
  attachmentUrl,
  isInlineSafe,
  safeFileName,
  targetKey,
  ulid,
} from '@orgus/protocol';
import type { Attachment, AttachmentTarget, AttachmentsState, Comment, CommentInput, CommentPatch, CommentTarget, CommentsState, Id } from '@orgus/protocol';
import type { AttachmentView, ResolvedAttachment, UploadFn } from '@orgus/ui';
import { ApiError, api, apiGet } from '../../lib/api.ts';
import { useOrgus, useExt } from '../../lib/store.ts';

const isMock = () => useOrgus.getState().mode === 'mock';
const EMPTY_ATTACHMENTS: AttachmentsState = { attachments: [], usage: { bytes: 0, quota: 2 * 1024 ** 3, count: 0, maxFile: ATTACHMENT_MAX_BYTES } };
const currentFiles = (): AttachmentsState => (useOrgus.getState().ext[ATTACHMENTS_EXT] as AttachmentsState | undefined) ?? EMPTY_ATTACHMENTS;
const currentComments = (): CommentsState => (useOrgus.getState().ext[COMMENTS_EXT] as CommentsState | undefined) ?? { comments: [] };
const putFiles = (data: AttachmentsState) => useOrgus.getState().applyExt({ ext: ATTACHMENTS_EXT, type: 'state', data });
const putComments = (data: CommentsState) => useOrgus.getState().applyExt({ ext: COMMENTS_EXT, type: 'state', data });

const MAX_INLINE_PIXELS = 40_000_000;

/** Uploads made in demo mode: id -> object URL of the file. */
const mockBlobs = new Map<Id, string>();
/** Demo-mode drafts (not attached yet). Live drafts are only known to the browser that made them. */
const drafts = new Map<Id, Attachment>();

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

export function toView(a: Attachment): AttachmentView {
  const blob = mockBlobs.get(a.id);
  // Huge pixel sizes (a decompression bomb) download instead of freezing the tab.
  const image = a.kind === 'image' && isInlineSafe(a.mime) && !(a.width && a.height && a.width * a.height > MAX_INLINE_PIXELS);
  return {
    id: a.id,
    name: a.name,
    size: a.size,
    mime: a.mime,
    isImage: image,
    url: blob ?? attachmentUrl(a.id, 'raw'),
    downloadUrl: blob ?? attachmentUrl(a.id, 'download'),
    ...(image ? { thumbUrl: blob ?? (a.thumb ? attachmentUrl(a.id, 'thumb') : attachmentUrl(a.id, 'raw')) } : {}),
    ...(a.width ? { width: a.width, height: a.height } : {}),
    by: a.by,
    createdAt: a.createdAt,
  };
}

export function useAttachmentsState(): AttachmentsState | undefined {
  const mode = useOrgus((x) => x.mode);
  const s = useExt<AttachmentsState>(ATTACHMENTS_EXT);
  // Demo mode starts with no files and no comments, held in the browser.
  useEffect(() => {
    if (mode === 'mock' && !s) putFiles(EMPTY_ATTACHMENTS);
  }, [mode, s]);
  return s;
}

/** Every attachment the hub has (attached ones), plus the drafts this browser made. */
export function useAttachmentIndex(): Map<Id, Attachment> {
  const s = useAttachmentsState();
  return useMemo(() => {
    const m = new Map<Id, Attachment>();
    for (const a of s?.attachments ?? []) m.set(a.id, a);
    for (const [id, a] of drafts) if (!m.has(id)) m.set(id, a);
    return m;
  }, [s]);
}

/** The files on one item, mission or comment, oldest first. */
export function useAttachmentsOn(target: AttachmentTarget | null): Attachment[] {
  const s = useAttachmentsState();
  const key = target ? targetKey(target) : '';
  return useMemo(() => (key ? (s?.attachments ?? []).filter((a) => a.target && targetKey(a.target) === key) : []), [s, key]);
}

/** Resolves attachment:<id> in Markdown against what the hub (or this browser) knows. */
export function useResolveAttachment(extra: readonly AttachmentView[] = []): (id: string) => ResolvedAttachment | null {
  const index = useAttachmentIndex();
  return useMemo(() => {
    const local = new Map(extra.map((v) => [v.id, v]));
    return (id: string) => {
      const v = local.get(id) ?? (index.get(id) ? toView(index.get(id)!) : undefined);
      return v ? { name: v.name, src: v.url, downloadUrl: v.downloadUrl, isImage: v.isImage } : null;
    };
  }, [index, extra]);
}

// ---------------------------------------------------------------------------
// Who is asking (for Remove and Edit)
// ---------------------------------------------------------------------------

export interface Caller {
  id: string;
  role: string;
  admin: boolean;
}

let callerPromise: Promise<Caller> | null = null;

export function useCaller(): Caller | null {
  const mode = useOrgus((s) => s.mode);
  const [c, setC] = useState<Caller | null>(null);
  useEffect(() => {
    if (mode === 'mock') return void setC({ id: 'owner', role: 'owner', admin: true });
    callerPromise ??= apiGet<Caller>('/api/attachments/caller').catch((e) => {
      callerPromise = null;
      throw e;
    });
    let live = true;
    callerPromise.then((x) => live && setC(x)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, [mode]);
  return c;
}

/** The text without any Markdown link or image that points at this attachment. */
export function stripAttachmentRef(body: string, id: Id): string {
  const safe = id.replace(/[^A-Za-z0-9_-]/g, '');
  return body.replace(new RegExp(String.raw`!?\[[^\]\n]*\]\(attachment:${safe}\)\n?`, 'g'), '');
}

export const canChange = (caller: Caller | null, by: string | undefined) => !!caller && (caller.admin || caller.id === by);

// ---------------------------------------------------------------------------
// Changes
// ---------------------------------------------------------------------------

export async function refreshAttachments(): Promise<void> {
  if (isMock()) return;
  putFiles(await apiGet<AttachmentsState>('/api/attachments'));
}

export async function refreshComments(): Promise<void> {
  if (isMock()) return;
  putComments(await apiGet<CommentsState>('/api/comments'));
}

function errorFrom(status: number, text: string, name: string): ApiError {
  try {
    const body = JSON.parse(text) as { error?: { what?: string; why?: string; next?: string; code?: string } };
    if (body.error?.what) return new ApiError({ what: body.error.what, why: body.error.why ?? '', next: body.error.next ?? 'Try again.', ...(body.error.code ? { code: body.error.code } : {}), status }, status);
  } catch {
    /* not JSON */
  }
  if (status === 413) return new ApiError({ what: `${name} is too large.`, why: 'The hub refused a file this size.', next: 'Make it smaller and try again.', status }, status);
  return new ApiError({ what: `${name} did not upload.`, why: status ? `The hub answered with status ${status}.` : 'The connection failed before the hub answered.', next: 'Check that Orgus is running, then retry.', status }, status);
}

/** Send a file to the hub with progress. `target` attaches it at once; without it, it is a draft. */
function xhrUpload(file: File, target: AttachmentTarget | null, onProgress: (f: number) => void, signal: AbortSignal): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const q = new URLSearchParams({ name: file.name });
    if (target) q.set('target', targetKey(target));
    xhr.open('POST', `/api/attachments?${q.toString()}`);
    xhr.setRequestHeader(CSRF_HEADER, '1');
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status === 200) {
        try {
          resolve(JSON.parse(xhr.responseText) as Attachment);
        } catch {
          reject(errorFrom(xhr.status, '', file.name));
        }
      } else reject(errorFrom(xhr.status, xhr.responseText, file.name));
    };
    xhr.onerror = () => reject(errorFrom(0, '', file.name));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/** A small preview made by the browser for images the hub cannot thumbnail itself (JPEG, GIF, WebP). */
async function sendThumb(a: Attachment, file: File): Promise<void> {
  if (a.kind !== 'image' || a.mime === 'image/png' || typeof createImageBitmap !== 'function') return;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 320 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
    if (!blob) return;
    await fetch(attachmentUrl(a.id, 'thumb'), { method: 'PUT', credentials: 'same-origin', headers: { [CSRF_HEADER]: '1', 'Content-Type': 'application/octet-stream' }, body: blob });
  } catch {
    /* a preview is optional */
  }
}

const sniffMock = (file: File): Pick<Attachment, 'mime' | 'kind'> => {
  if (/^image\/(png|jpeg|gif|webp)$/.test(file.type)) return { mime: file.type, kind: 'image' };
  if (file.type === 'text/plain') return { mime: 'text/plain', kind: 'text' };
  return { mime: 'application/octet-stream', kind: 'file' };
};

/** Upload a file; with a target it is attached at once, else it waits as a draft until attachTo. */
export async function uploadAttachment(file: File, target: AttachmentTarget | null, onProgress: (f: number) => void, signal: AbortSignal): Promise<Attachment> {
  if (file.size > currentFiles().usage.maxFile) {
    throw new ApiError({ what: `${file.name} is too large.`, why: `The hub accepts files up to ${Math.round(currentFiles().usage.maxFile / 1024 / 1024)} MB.`, next: 'Make it smaller and try again.' });
  }
  if (isMock()) {
    for (let i = 1; i <= 5; i++) {
      if (signal.aborted) throw new DOMException('aborted', 'AbortError');
      await new Promise((r) => setTimeout(r, 60));
      onProgress(i / 5);
    }
    const a: Attachment = { id: `att_${ulid()}`, sha256: '0'.repeat(64), name: safeFileName(file.name), ...sniffMock(file), size: file.size, thumb: false, by: 'owner', createdAt: Date.now(), target };
    mockBlobs.set(a.id, URL.createObjectURL(file));
    if (target) {
      const s = currentFiles();
      putFiles({ ...s, attachments: [...s.attachments, a], usage: { ...s.usage, bytes: s.usage.bytes + a.size, count: s.usage.count + 1 } });
    } else drafts.set(a.id, a);
    return a;
  }
  const a = await xhrUpload(file, target, onProgress, signal);
  if (!target) drafts.set(a.id, a);
  void sendThumb(a, file);
  if (target) await refreshAttachments().catch(() => undefined);
  return a;
}

/** An UploadFn for the design-system inputs, bound to a target (or drafts). */
export function uploaderFor(target: AttachmentTarget | null): UploadFn {
  return async (file, { onProgress, signal }) => toView(await uploadAttachment(file, target, onProgress, signal));
}

/** Attach uploaded drafts to an item or mission (after it was created). */
export async function attachTo(ids: readonly Id[], target: AttachmentTarget): Promise<void> {
  if (!ids.length) return;
  if (isMock()) {
    const s = currentFiles();
    const moved = ids.map((id) => drafts.get(id)).filter((a): a is Attachment => !!a).map((a) => ({ ...a, target }));
    for (const a of moved) drafts.delete(a.id);
    putFiles({ ...s, attachments: [...s.attachments.filter((a) => !ids.includes(a.id)), ...moved] });
    return;
  }
  for (const id of ids) {
    await api(`/api/attachments/${encodeURIComponent(id)}`, { method: 'PATCH', body: { target } });
    drafts.delete(id);
  }
  await refreshAttachments().catch(() => undefined);
}

export async function removeAttachment(id: Id): Promise<void> {
  if (isMock()) {
    drafts.delete(id);
    const s = currentFiles();
    putFiles({ ...s, attachments: s.attachments.filter((a) => a.id !== id) });
    return;
  }
  await api(`/api/attachments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  drafts.delete(id);
  await refreshAttachments().catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export function useCommentsOn(target: CommentTarget): Comment[] {
  const s = useExt<CommentsState>(COMMENTS_EXT);
  const key = `${target.kind}:${target.id}`;
  return useMemo(() => (s?.comments ?? []).filter((c) => `${c.target.kind}:${c.target.id}` === key), [s, key]);
}

/** True once the hub has said whether it has a comments module (false on older hubs). */
export function useCommentsAvailable(): boolean | null {
  const mode = useOrgus((s) => s.mode);
  const phase = useOrgus((s) => s.phase);
  const s = useExt<CommentsState>(COMMENTS_EXT);
  useEffect(() => {
    if (mode === 'mock' && !s) putComments({ comments: [] });
  }, [mode, s]);
  if (mode === 'mock') return true;
  if (phase !== 'ready') return null;
  return !!s;
}

export async function addComment(input: CommentInput): Promise<Comment> {
  if (isMock()) {
    const c: Comment = { id: `cmt_${ulid()}`, target: input.target, body: input.body, by: 'owner', createdAt: Date.now(), editedAt: null, attachmentIds: input.attachmentIds ?? [] };
    if (c.attachmentIds.length) await attachTo(c.attachmentIds, { kind: 'comment', id: c.id });
    putComments({ comments: [...currentComments().comments, c] });
    return c;
  }
  const c = await api<Comment>('/api/comments', { method: 'POST', body: input });
  for (const id of input.attachmentIds ?? []) drafts.delete(id);
  await Promise.all([refreshComments(), refreshAttachments()]).catch(() => undefined);
  return c;
}

export async function editComment(id: Id, patch: CommentPatch): Promise<Comment> {
  if (isMock()) {
    const prev = currentComments().comments.find((c) => c.id === id);
    if (!prev) throw new ApiError({ what: 'The comment was not changed.', why: 'It is not known any more.', next: 'Reload the page.' });
    const add = (patch.attachmentIds ?? []).filter((x) => !prev.attachmentIds.includes(x));
    if (add.length) await attachTo(add, { kind: 'comment', id });
    const next: Comment = { ...prev, ...(patch.body !== undefined ? { body: patch.body } : {}), attachmentIds: [...prev.attachmentIds, ...add], editedAt: Date.now() };
    putComments({ comments: currentComments().comments.map((c) => (c.id === id ? next : c)) });
    return next;
  }
  const c = await api<Comment>(`/api/comments/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
  await Promise.all([refreshComments(), refreshAttachments()]).catch(() => undefined);
  return c;
}

export async function deleteComment(id: Id): Promise<void> {
  if (isMock()) {
    putComments({ comments: currentComments().comments.filter((c) => c.id !== id) });
    const s = currentFiles();
    putFiles({ ...s, attachments: s.attachments.filter((a) => !(a.target?.kind === 'comment' && a.target.id === id)) });
    return;
  }
  await api(`/api/comments/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await Promise.all([refreshComments(), refreshAttachments()]).catch(() => undefined);
}

/** The files a mission start copies to the machine: the item's, the mission's and those on their comments. */
export function useMissionBundle(itemId: Id | null | undefined, missionId: Id | null | undefined): Attachment[] {
  const files = useAttachmentsState();
  const comments = useExt<CommentsState>(COMMENTS_EXT);
  return useMemo(() => {
    const keys = new Set<string>();
    const owners = [...(itemId ? [`item:${itemId}`] : []), ...(missionId ? [`mission:${missionId}`] : [])];
    for (const k of owners) keys.add(k);
    for (const c of comments?.comments ?? []) if (owners.includes(`${c.target.kind}:${c.target.id}`)) keys.add(`comment:${c.id}`);
    return (files?.attachments ?? []).filter((a) => a.target && keys.has(targetKey(a.target)));
  }, [files, comments, itemId, missionId]);
}
